import React, { useState, useEffect, useCallback } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { NoteStatus } from "../../interfaces/mortgageNote";
import { FetchNotesParams } from "../../redux/interfaces/mortgageNote.redux.interface";
import { UnitedStatesState } from "../../data/unitedStates";
import capitalize from "../../helpers/capitalize";
import states from "../../data/unitedStates";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faAngleDown, faXmark } from "@fortawesome/pro-solid-svg-icons";
import FilterValueRange, { Option } from "./FilterValueRange";
import addCommas from "../../helpers/addCommas";

interface FilterToolbarProps {
  onFilterChange: (filters: Partial<FetchNotesParams>) => void;
}

const FilterToolbar: React.FC<FilterToolbarProps> = ({ onFilterChange }) => {
  const location = useLocation();
  const navigate = useNavigate();
  const [selectedStates, setSelectedStates] = useState<string[]>([]);
  const [targetROR, setTargetROR] = useState<
    [number | undefined, number | undefined]
  >([undefined, undefined]);
  const [loanAmount, setLoanAmount] = useState<
    [number | undefined, number | undefined]
  >([undefined, undefined]);
  const [isInitialized, setIsInitialized] = useState(false);
  const [currentFilters, setCurrentFilters] = useState<
    Partial<FetchNotesParams>
  >({});

  const parseURLParams = useCallback(() => {
    const searchParams = new URLSearchParams(location.search);

    const statesParam = searchParams.get("addressState");
    const statusParam = searchParams.get("status");
    const targetMin = searchParams.get("targetMin");
    const targetMax = searchParams.get("targetMax");
    const loanMin = searchParams.get("loanMin");
    const loanMax = searchParams.get("loanMax");

    return {
      addressState: statesParam ? statesParam.split(",") : [],
      status: statusParam
        ? (statusParam.split(",") as NoteStatus[])
        : undefined,
      targetMin: targetMin ? parseInt(targetMin) : undefined,
      targetMax: targetMax ? parseInt(targetMax) : undefined,
      loanMin: loanMin ? parseInt(loanMin) : undefined,
      loanMax: loanMax ? parseInt(loanMax) : undefined,
    };
  }, [location.search]);

  useEffect(() => {
    if (!isInitialized) {
      const params = parseURLParams();
      setSelectedStates(params.addressState);
      setTargetROR([params.targetMin, params.targetMax]);
      setLoanAmount([params.loanMin, params.loanMax]);

      const defaultStatus = params.status || ["Available"];
      const initialFilters = { ...params, status: defaultStatus };

      setCurrentFilters(initialFilters);
      onFilterChange(initialFilters);
      setIsInitialized(true);
    }
  }, [isInitialized, parseURLParams, onFilterChange]);

  const updateURLParams = useCallback(
    (params: Partial<FetchNotesParams>) => {
      const searchParams = new URLSearchParams();

      Object.entries(params).forEach(([key, value]) => {
        if (value !== undefined && value !== null) {
          if (Array.isArray(value)) {
            searchParams.set(key, value.join(","));
          } else {
            searchParams.set(key, value.toString());
          }
        }
      });

      const newSearch = searchParams.toString();
      navigate(`${location.pathname}${newSearch ? `?${newSearch}` : ""}`, {
        replace: true,
      });
    },
    [location.pathname, navigate]
  );

  const updateFilters = useCallback(
    (newFilters: Partial<FetchNotesParams>) => {
      // Merge new filters with existing filters
      const updatedFilters = { ...currentFilters, ...newFilters };

      // Remove keys with undefined, null, or empty array values, except for status
      Object.keys(updatedFilters).forEach((key) => {
        const value = updatedFilters[key as keyof FetchNotesParams];
        if (
          value === undefined ||
          value === null ||
          (Array.isArray(value) && value.length === 0)
        ) {
          if (key !== "status") {
            // Ensure status is not removed
            delete updatedFilters[key as keyof FetchNotesParams];
          }
        }
      });

      setCurrentFilters(updatedFilters);
      onFilterChange(updatedFilters);
      updateURLParams(updatedFilters);
    },
    [currentFilters, onFilterChange, updateURLParams]
  );

  const handleStatusFilter = useCallback(
    (status: NoteStatus[] | null) => {
      if (status === null) {
        const { status: _, ...restFilters } = currentFilters;
        updateFilters(restFilters);
      } else {
        updateFilters({ ...currentFilters, status });
      }
    },
    [currentFilters, updateFilters]
  );

  const handleStateFilter = useCallback(
    (state: string) => {
      const updatedStates = selectedStates.includes(state)
        ? selectedStates.filter((s) => s !== state)
        : [...selectedStates, state];
      setSelectedStates(updatedStates);
      updateFilters({
        addressState: updatedStates.length > 0 ? updatedStates : undefined,
      });
    },
    [selectedStates, updateFilters]
  );

  const handleTargetRORChange = useCallback(
    (min: number | undefined, max: number | undefined) => {
      setTargetROR([min, max]);
      updateFilters({
        targetMin: min !== undefined ? min : undefined,
        targetMax: max !== undefined ? max : undefined,
      });
    },
    [updateFilters]
  );

  const handleLoanAmountChange = useCallback(
    (min: number | undefined, max: number | undefined) => {
      setLoanAmount([min, max]);
      updateFilters({
        loanMin: min !== undefined ? min : undefined,
        loanMax: max !== undefined ? max : undefined,
      });
    },
    [updateFilters]
  );

  return (
    <div className="w-full space-y-4 mt-1 mb-4">
      <div className="flex flex-col md:flex-row justify-between w-full gap-4">
        <button
          className="btn bg-primary rounded-[4px] border-primary border-2  text-white hover:border-primary hover:text-primary hover:bg-white flex-1 font-bold"
          onClick={() => handleStatusFilter(["Available"])}
        >
          AVAILABLE MORTGAGE NOTES
        </button>
        <button
          className="btn bg-secondary rounded-[4px] border-secondary border-2 text-white hover:border-secondary hover:text-secondary hover:bg-white flex-1 font-bold"
          onClick={() => handleStatusFilter(null)}
        >
          ALL NOTES
        </button>
        <button
          className="btn bg-accent rounded-[4px] border-accent border-2 text-white hover:border-accent hover:text-accent hover:bg-white flex-1 font-bold"
          onClick={() => handleStatusFilter(["Funded"])}
        >
          SOLD MORTGAGE NOTES
        </button>
        <button
          className="btn bg-success rounded-[4px] border-success border-2 text-white hover:border-success hover:text-success hover:bg-white flex-1 font-bold"
          onClick={() => handleStatusFilter(["Paid Off"])}
        >
          SUCCESSFULLY PAID OFF
        </button>
      </div>
      <div className="flex justify-between w-full gap-4">
        <div className="dropdown cursor-pointer flex-1">
          <div
            tabIndex={0}
            role="button"
            className="btn btn-outline w-full mb-0.5 font-bold relative hover:bg-white hover:border-secondary text-main-gray text-xs sm:text-sm md:text-lg rounded-[6px]"
          >
            Select State(s)
            <div className="absolute right-2">
              <FontAwesomeIcon icon={faAngleDown} className="" />
            </div>
          </div>
          <ul
            tabIndex={0}
            className="menu dropdown-content z-50 bg-black rounded-lg w-48"
          >
            {states.map((state: UnitedStatesState, index: number) => (
              <label
                key={`united-states-state-${index}`}
                className="flex items-center py-[1px]"
              >
                <input
                  type="checkbox"
                  className="checkbox checkbox-xs"
                  checked={selectedStates.includes(state.value)}
                  onChange={() => handleStateFilter(state.value)}
                />
                <span className="ml-2 select-none cursor-pointer">
                  {capitalize(state.name)}
                </span>
              </label>
            ))}
          </ul>
        </div>
        <div className="dropdown cursor-pointer flex-1">
          <div
            tabIndex={0}
            role="button"
            className="btn btn-outline w-full mb-0.5 font-bold relative hover:bg-white hover:border-secondary text-main-gray text-xs sm:text-sm md:text-lg rounded-[6px]"
          >
            Targeted ROR
            <div className="absolute right-2">
              <FontAwesomeIcon icon={faAngleDown} className="" />
            </div>
          </div>
          <ul className="menu dropdown-content z-50 bg-black rounded-lg">
            <FilterValueRange
              minValues={rorMinValues}
              maxValues={rorMaxValues}
              state={{ minValue: targetROR[0], maxValue: targetROR[1] }}
              setState={(newState: {
                minValue: number | undefined;
                maxValue: number | undefined;
              }) => {
                handleTargetRORChange(newState.minValue, newState.maxValue);
              }}
            />
          </ul>
        </div>
        <div className="dropdown cursor-pointer flex-1 dropdown-end">
          <div
            tabIndex={0}
            role="button"
            className="btn btn-outline w-full mb-0.5 font-bold relative hover:bg-white hover:border-secondary text-main-gray text-xs sm:text-sm md:text-lg rounded-[6px]"
          >
            Loan Amount
            <div className="absolute right-2">
              <FontAwesomeIcon icon={faAngleDown} className="" />
            </div>
          </div>
          <ul className="menu dropdown-content z-50 bg-black rounded-lg">
            <FilterValueRange
              minValues={loanAmountMinValues}
              maxValues={loanAmountMaxValues}
              state={{ minValue: loanAmount[0], maxValue: loanAmount[1] }}
              setState={(newState: {
                minValue: number | undefined;
                maxValue: number | undefined;
              }) => {
                handleLoanAmountChange(newState.minValue, newState.maxValue);
              }}
            />
          </ul>
        </div>
      </div>
      <div className="flex flex-wrap gap-2 mt-4">
        <button
          className="btn btn-sm btn-error text-white"
          onClick={() => {
            setSelectedStates([]);
            setTargetROR([undefined, undefined]);
            setLoanAmount([undefined, undefined]);

            // Define the cleared filters with default status
            const clearedFilters: Partial<FetchNotesParams> = {
              status: ["Available"],
            };

            // Update filters and URL with cleared filters
            updateFilters(clearedFilters);
            updateURLParams(clearedFilters);
          }}
        >
          Clear All Filters
        </button>
        {selectedStates.map((state) => (
          <div
            key={state}
            className="bg-gray-300 rounded-lg flex items-center px-2 py-0.5"
          >
            <button
              className=" text-gray-500 font-semibold"
              onClick={() => handleStateFilter(state)}
            >
              <FontAwesomeIcon icon={faXmark} />
            </button>
            <div className="ml-2 text-black font-semibold">{state}</div>
          </div>
        ))}
        {(targetROR[0] !== undefined || targetROR[1] !== undefined) && (
          <div className="bg-gray-300 rounded-lg flex items-center px-2 py-0.5 text-black">
            <button
              className=" text-gray-500 font-semibold"
              onClick={() => handleTargetRORChange(undefined, undefined)}
            >
              <FontAwesomeIcon icon={faXmark} />
            </button>
            <div className="ml-2 text-black font-semibold">
              ROR: {targetROR[0] !== undefined ? `${targetROR[0]}%` : "Min"} -{" "}
              {targetROR[1] !== undefined ? `${targetROR[1]}%` : "Max"}
            </div>
          </div>
        )}
        {(loanAmount[0] !== undefined || loanAmount[1] !== undefined) && (
          <div className="bg-gray-300 rounded-lg flex items-center px-2 py-0.5 text-black">
            <button
              className=" text-gray-500 font-semibold"
              onClick={() => handleLoanAmountChange(undefined, undefined)}
            >
              <FontAwesomeIcon icon={faXmark} />
            </button>
            <div className="ml-2 text-black font-semibold">
              Loan: $
              {loanAmount[0] !== undefined
                ? `${addCommas(loanAmount[0])}`
                : "0"}{" "}
              - $
              {loanAmount[1] !== undefined
                ? `${addCommas(loanAmount[1])}`
                : "Max"}
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default FilterToolbar;

const rorMinValues: Option[] = [
  {
    value: 8,
    label: "8%",
  },
  {
    value: 9,
    label: "9%",
  },
  {
    value: 10,
    label: "10%",
  },
  {
    value: 11,
    label: "11%",
  },
  {
    value: 12,
    label: "12%",
  },
];

const rorMaxValues: Option[] = [
  {
    value: 8,
    label: "8%",
  },
  {
    value: 9,
    label: "9%",
  },
  {
    value: 10,
    label: "10%",
  },
  {
    value: 11,
    label: "11%",
  },
  {
    value: 12,
    label: "12%",
  },
];

const loanAmountMinValues: Option[] = [
  { value: 50000, label: "$50,000" },
  { value: 100000, label: "$100,000" },
  { value: 150000, label: "$150,000" },
  { value: 200000, label: "$200,000" },
  { value: 250000, label: "$250,000" },
  { value: 300000, label: "$300,000" },
  { value: 350000, label: "$350,000" },
  { value: 400000, label: "$400,000" },
  { value: 450000, label: "$450,000" },
  { value: 500000, label: "$500,000" },
];

const loanAmountMaxValues: Option[] = [
  { value: 50000, label: "$50,000" },
  { value: 100000, label: "$100,000" },
  { value: 150000, label: "$150,000" },
  { value: 200000, label: "$200,000" },
  { value: 250000, label: "$250,000" },
  { value: 300000, label: "$300,000" },
  { value: 350000, label: "$350,000" },
  { value: 400000, label: "$400,000" },
  { value: 450000, label: "$450,000" },
  { value: 500000, label: "$500,000" },
];
